import { Query } from '@apollo/client/react/components';
import cx from 'classnames';
import * as React from 'react';
import Emoji from 'components/Emoji';
import CurrentUser from 'auth/CurrentUser';
import Tooltip from 'components/Tooltip';
import LayerTrigger from 'components/LayerTrigger';
import REACTION_SETTINGS_QUERY from 'user/queries/reactionSettings.gql';
import type { ReactionSettings } from 'user/queries/__generated__/ReactionSettings';
import type { ReactionSummaryFragment_reactions as Reaction } from './Reaction/__generated__/ReactionSummaryFragment';

import './styles/MessageReaction.scss';

type Props = {
  reaction: Reaction;
  disableReactionToggle?: boolean;
  addReactionToSummary: (name: string) => void;
  removeReactionFromSummary: (name: string) => void;
};

export type InnerProps = Props & {
  reactionInventory: Map<string, string>;
};

const MAX_NAMES = 10;

export function MessageReaction(props: InnerProps) {
  const { reaction, reactionInventory } = props;
  const reactionName = props.reaction.name;
  if (!reactionName) {
    return null;
  }

  const unicodeChar = reactionInventory.get(reactionName);
  if (!unicodeChar) {
    return null;
  }

  const hasBeenSelectedByCurrentUser = reaction.users.some(
    (u) => u.uuid === CurrentUser.getUserUuid()
  );

  function toggleReaction(): void {
    if (props.disableReactionToggle) {
      return;
    }

    if (hasBeenSelectedByCurrentUser) {
      props.removeReactionFromSummary(reactionName || '');
    } else {
      props.addReactionToSummary(reactionName || '');
    }
  }

  const tooltip = (
    <Tooltip>
      <ul>{makeListy(replaceYourNameWithTheWordYouIn(reaction.users.map((u) => u.name)))}</ul>
    </Tooltip>
  );

  return (
    <LayerTrigger trigger={['hover', 'focus']} layer={tooltip} placement="bottom">
      <div
        className={cx('MessageReaction', {
          'MessageReaction-selfReacted': hasBeenSelectedByCurrentUser,
        })}
        onClick={toggleReaction}
      >
        <Emoji className="MessageReaction-emoji" character={unicodeChar} />
        {reaction.count}
      </div>
    </LayerTrigger>
  );
}

export default function MessageReactionContainer(props: Props) {
  return (
    <Query<ReactionSettings> query={REACTION_SETTINGS_QUERY}>
      {({ data }) => {
        const reactions = data?.settings?.reactionInventory?.reactions || [];
        const reactionInventory = new Map<string, string>(
          reactions.map((r) => [r.name, r.unicode])
        );

        if (!reactionInventory) {
          return null;
        }

        return <MessageReaction {...props} reactionInventory={reactionInventory} />;
      }}
    </Query>
  );
}

export function replaceYourNameWithTheWordYouIn(names: Array<string>): Array<string> {
  return names
    .map((name) => (name === CurrentUser.getUser().getName() ? 'You' : name))
    .sort((a, b) => {
      if (a === 'You') {
        return -1;
      }

      if (b === 'You') {
        return 1;
      }

      return a.localeCompare(b);
    });
}

export function makeListy(
  names: string[],
  maxNamed: number = MAX_NAMES
): Array<React.ReactElement> {
  const toName = names.slice(0, maxNamed).map((n) => <li key={n}>{n}</li>);

  const remainingCount = names.length - maxNamed;

  if (remainingCount > 0) {
    return [...toName, <li key="&rest">{`& ${remainingCount} more...`}</li>];
  }

  return toName;
}
